import { useEffect, useState } from 'react';
import axios from 'axios';
import { normalizeSeverity } from '@/lib/severity';

/**
 * Response-time targets. A brief is due a fixed number of hours after it was
 * matched to the organization; the hours come from the organization's SLA
 * policy (/api/inbox/sla-policy), with these defaults until it loads.
 * Informational briefs carry no target.
 */
export const DEFAULT_SLA_HOURS = { Critical: 24, High: 72, Medium: 168, Low: 720 };
export const HANDLED = new Set(['acked', 'shipped', 'archived']);
const SOON_MS = 8 * 3600e3;

let cached = null;
let inflight = null;
function loadPolicy() {
  if (!inflight) {
    inflight = axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/inbox/sla-policy`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    }).then((r) => { cached = { ...DEFAULT_SLA_HOURS, ...(r.data?.hours || {}) }; return cached; })
      .catch(() => { inflight = null; return DEFAULT_SLA_HOURS; });
  }
  return inflight;
}

/** After Settings saves a new policy, every page that reads it picks the new hours up. */
export function setSlaPolicyCache(hours) {
  cached = { ...DEFAULT_SLA_HOURS, ...hours };
  inflight = Promise.resolve(cached);
}

export function useSlaPolicy() {
  const [hours, setHours] = useState(cached || DEFAULT_SLA_HOURS);
  useEffect(() => {
    let live = true;
    loadPolicy().then((h) => { if (live) setHours(h); });
    return () => { live = false; };
  }, []);
  return hours;
}

/** "5h", "2d", "45m" for a duration in ms. Hours up to two days: they matter at this scale. */
export function duration(ms) {
  const h = Math.abs(ms) / 3600e3;
  if (h < 1) return `${Math.max(1, Math.round(h * 60))}m`;
  if (h < 48) return `${Math.round(h)}h`;
  return `${Math.round(h / 24)}d`;
}

export const slaWindowLabel = (hours) => (hours < 168 ? `${hours} hours` : `${Math.round(hours / 24)} days`);

/**
 * Where a brief stands against its target.
 *   state: 'overdue' | 'soon' | 'ok' | 'met' | 'missed' | 'handled' | null (no target)
 */
export function slaFor(brief, status, item, hours = DEFAULT_SLA_HOURS, now = Date.now()) {
  const sev = normalizeSeverity(brief?.severity);
  if (!hours[sev]) return null;
  const start = Date.parse(brief.linked_at || brief.discovered_at);
  if (Number.isNaN(start)) return null;
  const due = start + hours[sev] * 3600e3;
  const base = { due, hours: hours[sev], severity: sev };
  if (HANDLED.has(status)) {
    const at = item?.updated_at ? Date.parse(item.updated_at) : NaN;
    if (Number.isNaN(at)) return { ...base, state: 'handled', label: 'Handled' };
    return at <= due ? { ...base, state: 'met', label: 'Met' } : { ...base, state: 'missed', label: `Missed by ${duration(at - due)}` };
  }
  const left = due - now;
  if (left < 0) return { ...base, state: 'overdue', label: `Overdue ${duration(left)}`, overdue: true };
  return { ...base, state: left < SOON_MS ? 'soon' : 'ok', label: `Due in ${duration(left)}` };
}
